/**
 * Job → 评估报表页链接（任务列表「报表」按钮用）。
 *
 * 报表只有感知（perception）Job 有入口：评估报表页 `/perception-report?jobId=` 按 jobId
 * 取数，汇总各检查项的 case 指标。PNC 及其它仿真类型不出「报表」按钮——它们能看的
 * 「单 Job 统计视图」（`/job-tasks/{jobId}?mode=stats`）就是点 Job 名进的同一个场景列表，
 * 多一个按钮只是重复入口。
 *
 * 仿真类型优先取行数据（后端 JobVo.simType），缺失时回退到页面 `?sim_type=`；
 * 两边都判不出感知时不渲染按钮（返回 null）。
 */
export function jobReportPath(
  jobId: number,
  rowSimType?: string | null,
  pageSimType?: string | null,
): string | null {
  const simType = rowSimType ?? pageSimType;
  if (simType !== 'perception') {
    return null;
  }
  return `/perception-report?jobId=${jobId}`;
}

/** 「报表」按钮可用性：不可点时的 `reason` 直接用作 Tooltip 文案。 */
export interface JobReportAvailability {
  enabled: boolean;
  reason?: string;
}

/** 已开始执行或可能保留部分评测产物的状态，报表自行展示覆盖数或空态。 */
const REPORT_READY_STATUSES: readonly number[] = [1, 10, 15, 20, 25, 30];

/** 尚未开始执行的状态。 */
const UNFINISHED_STATUSES: readonly number[] = [3, 5, 7];

/**
 * Job 状态 → 「报表」按钮是否可点。
 *
 * 执行中、执行异常或中止也可能有已完成场景的评测结果，允许查看部分报表。
 * 未开始 / 排队中 / 编译中 / 编译失败尚无运行结果，保持置灰。
 *
 * `status` 缺失（老后端不返回）时不拦，保持可点，避免因字段缺失整列按钮失效；
 * 未知的新状态按白名单处理（置灰 + 兜底文案），新状态接入时记得回这里登记。
 */
export function jobReportAvailability(
  status?: number | null,
  statusName?: string | null,
): JobReportAvailability {
  if (status == null || REPORT_READY_STATUSES.includes(status)) {
    return { enabled: true };
  }
  const label = statusName || `状态 ${status}`;
  return {
    enabled: false,
    reason: UNFINISHED_STATUSES.includes(status)
      ? `任务${label}：尚未开始执行，没有场景评测结果。`
      : `任务${label}：暂无可查看的场景评测结果。`,
  };
}
